import { useState } from "react";
import {
  buildIndexedArray,
  currencySymbols,
  InputVariantTypes,
  Location,
  locationCurrencyCodeMapper,
  projectLocationAddonMapper,
  toCurrency,
} from "@ecologi/shared";
import { LocalProjectInfoUK } from "./LocalProjectInfoUK";
import { LocalProjectInfoAU } from "./LocalProjectInfoAU";
import { LocalProjectInfoUS } from "./LocalProjectInfoUS";
import { ModalBase } from "../../ModalBase";
import FlagIcon from "@ecologi/assets/icons/Plan/flag.svg";
import SpadeIcon from "@ecologi/assets/icons/Plan/spade.svg";
import MegaphoneIcon from "@ecologi/assets/icons/Plan/megaphone.svg";
import OpenModal from "@ecologi/assets/icons/Plan/open_modal.svg";
import AuFlag from "@ecologi/assets/icons/Flags/au.svg";
import UkFlag from "@ecologi/assets/icons/Flags/uk.svg";
import UsFlag from "@ecologi/assets/icons/Flags/us.svg";
import styles from "../styles/LocalProjectAddonBanner.module.scss";
import { Select } from "../../Select";

interface Props {
  numLocalTrees: number;
  setNumLocalTrees: (numTrees: number) => void;
  location: Location | null;
}

const numberOptions = buildIndexedArray(101).map((n) => ({
  value: n.toString(),
  label: n.toString(),
}));

export const LocalProjectAddonBanner = ({
  numLocalTrees,
  setNumLocalTrees,
  location,
}: Props) => {
  const [isMoreInfoOpen, setMoreInfoState] = useState(false);

  // We only show this if we support a local reforestation project
  // in their region.
  if (!location) {
    return null;
  }

  const currencyCode = locationCurrencyCodeMapper[location.countryCode];
  const currencySymbol = currencySymbols[currencyCode];
  const addon = projectLocationAddonMapper[location.countryCode];

  if (!addon) {
    return null;
  }

  let MoreInfoCopy = LocalProjectInfoUK;
  let moreInfoTitle = "Reforestation at home in the UK";
  let CountryFlag = UkFlag;

  if (location.countryCode === "US") {
    MoreInfoCopy = LocalProjectInfoUS;
    moreInfoTitle = "Reforesting Appalachia";
    CountryFlag = UsFlag;
  } else if (location.countryCode === "AU") {
    MoreInfoCopy = LocalProjectInfoAU;
    moreInfoTitle = "Repair Australia’s landscape";
    CountryFlag = AuFlag;
  }

  return (
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <h2 className={styles.title}>
          Add a local project to your monthly plan
        </h2>
        <div className={styles.card}>
          <div className={styles.leftCol}>
            <h3 className={styles.cardTitle}>
              Plant extra trees in {addon.countryNiceCopyShort}
              <CountryFlag width={32} height={20} />
            </h3>
            <p>
              Bring the impact closer to home by supporting a tree planting
              project in {addon.countryNiceCopyShort}.
            </p>
            <p
              className={styles.moreInfo}
              onClick={() => setMoreInfoState(true)}
            >
              Read more about our {addon.country} tree planting projects{" "}
              <OpenModal />
            </p>
            <div className={styles.contentFooter}>
              <div className={styles.treePrice}>
                <div className={styles.treePriceTitle}>
                  {addon.country} tree
                </div>
                {currencySymbol}
                {toCurrency(addon.price)} / month
              </div>
              <div className={styles.treeQuantity}>
                <Select
                  items={numberOptions}
                  value={numLocalTrees.toString()}
                  onChange={(e) => {
                    let numTrees = Number(e.target.value) || 0;
                    numTrees = numTrees < 1000 ? numTrees : 999;
                    setNumLocalTrees(numTrees);
                  }}
                  hiddenLabel
                  label="Number of local trees"
                  name="number"
                  variant={InputVariantTypes.SNOWDROP}
                />
              </div>
            </div>
          </div>
          <div className={styles.rightCol}>
            <div className={styles.treePhoto} data-country={addon.country} />
            <div className={styles.benefitsWrap}>
              <div className={styles.benefit}>
                <FlagIcon className={styles.benefitIcon} /> Special tile in your
                forest
              </div>
              <div className={styles.benefit}>
                <SpadeIcon className={styles.benefitIcon} /> Returning mining,
                logging, and agricultural land to balance
              </div>
              <div className={styles.benefit}>
                <MegaphoneIcon className={styles.benefitIcon} /> Updates & news
                from the project
              </div>
            </div>
          </div>
        </div>
        <ModalBase
          title={moreInfoTitle}
          isOpen={isMoreInfoOpen}
          onClose={() => setMoreInfoState(false)}
        >
          <MoreInfoCopy />
        </ModalBase>
      </div>
    </section>
  );
};
